import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { ChatBotProvider } from 'react-chatbotify';
import { Fab, Tab, Tabs, Tooltip } from '@mui/material';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';

import Modal, {
  Ref as ModalRef,
} from 'components/AlarmManagement/components/Modal/Modal';
import AIChatbot, { Ref as AIChatbotRef } from './Chatbot/AIChatbot';

import {
  generateUUID,
  AIApplication,
  AIAssistantTabs,
  closeAIApiCallEvent,
} from './helper';

import AIIcon from '../../assets/images/ai-icon.svg';

const tabs = ['Trouble Ticket Assistant', 'User Guide', 'Telco Assistant', 'Integration Assistant'];

interface Props {
  newTab?: boolean;
  showFabIcon?: boolean;
}

export interface Ref {
  handleOpenAIAssistantModal: () => void;
}

const AIAssistant: ForwardRefRenderFunction<Ref, Props> = (
  { newTab = false, showFabIcon = false },
  ref
) => {
  const modalRef = useRef<ModalRef>(null);
  const userGuideRef = useRef<AIChatbotRef>(null);
  const smartTicketingRef = useRef<AIChatbotRef>(null);
  const userGuideChatbotId = useRef<string>(generateUUID());
  const smartTicketingChatbotId = useRef<string>(generateUUID());
  const userGuideEventSourceRef = useRef<EventSource | null>(null);
  const smartTicketingEventSourceRef = useRef<EventSource | null>(null);

  const assistantRef = useRef<AIChatbotRef>(null);
  const assistantChatbotId = useRef<string>(generateUUID());
  const assistantEventSourceRef = useRef<EventSource | null>(null);


  
  const integrationRef = useRef<AIChatbotRef>(null);
  const integrationChatbotId = useRef<string>(generateUUID());
  const integrationEventSourceRef = useRef<EventSource | null>(null);
  
  const [selectedTab, setSelectedTab] =
    useState<AIAssistantTabs>('Telco Assistant');

  const handleChangeTabs = (
    event: React.SyntheticEvent,
    newValue: AIAssistantTabs
  ) => setSelectedTab(newValue);

  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, '_blank');
    if (newWindow) newWindow.opener = null;
  };

  const handleOpenAIAssistantModal = () => {
    modalRef.current?.handleShowModal(true);
    setTimeout(() => {
      switch (selectedTab) {
        case 'Trouble Ticket Assistant': {
          smartTicketingEventSourceRef.current?.dispatchEvent(
            closeAIApiCallEvent
          );
          break;
        }
        case 'User Guide': {
          userGuideEventSourceRef.current?.dispatchEvent(closeAIApiCallEvent);
          break;
        }
        case 'Telco Assistant': {
          assistantEventSourceRef.current?.dispatchEvent(closeAIApiCallEvent);
          break;
        }
        case 'Integration Assistant': {
          integrationEventSourceRef.current?.dispatchEvent(closeAIApiCallEvent);
          break;
        }
      }
    }, 100);
  };

  const handleActionButton = () => {
    setTimeout(() => {
      switch (selectedTab) {
        case 'Trouble Ticket Assistant': {
          smartTicketingEventSourceRef.current?.dispatchEvent(
            closeAIApiCallEvent
          );
          smartTicketingRef.current?.handleResetChat();
          break;
        }
        case 'User Guide': {
          userGuideEventSourceRef.current?.dispatchEvent(closeAIApiCallEvent);
          userGuideRef.current?.handleResetChat();
          break;
        }
        case 'Telco Assistant': {
          assistantEventSourceRef.current?.dispatchEvent(closeAIApiCallEvent);
          assistantRef.current?.handleResetChat();
          break;
        }
        case 'Integration Assistant': {
          integrationEventSourceRef.current?.dispatchEvent(closeAIApiCallEvent);
          integrationRef.current?.handleResetChat();
          break;
        }
      }
    }, 550);
  };

  const handleCloseActionButton = () => {
    setTimeout(() => {
      switch (selectedTab) {
        case 'Trouble Ticket Assistant': {
          smartTicketingEventSourceRef.current?.dispatchEvent(
            closeAIApiCallEvent
          );
          smartTicketingRef.current?.handleResetChat();
          break;
        }
        case 'User Guide': {
          userGuideEventSourceRef.current?.dispatchEvent(closeAIApiCallEvent);
          userGuideRef.current?.handleResetChat();
          break;
        } 
        case 'Telco Assistant': {
          assistantEventSourceRef.current?.dispatchEvent(closeAIApiCallEvent);
          assistantRef.current?.handleResetChat();
          break;
        }
        case 'Integration Assistant': {
          integrationEventSourceRef.current?.dispatchEvent(closeAIApiCallEvent);
          integrationRef.current?.handleResetChat();
          break;
        }
      }
      setSelectedTab('Telco Assistant');
    }, 550);
  };

  const getActiveApplication = (tab: AIAssistantTabs): AIApplication => {
    switch (tab) {
      case 'Trouble Ticket Assistant':
        return AIApplication.SMART_TICKETING;
      case 'User Guide':
        return AIApplication.USER_GUIDE;
      case 'Telco Assistant':
        return AIApplication.ASSISTANT;
      case 'Integration Assistant':
        return AIApplication.INTEGRATION_ASSISTANT;
      default:
        return AIApplication.ASSISTANT;
    }
  };
 
  const closeModal = () => { 
    modalRef.current?.handleShowModal(false);
  };

  useImperativeHandle(ref, () => ({
    handleOpenAIAssistantModal,
  }));

  const TitleElement = (
    <span className="title-container">
      <Tooltip
        title={
          'AI Chatbot powered by generative AI technology using the Mistral LLM model for enhanced multilingual user support.'
        }
        style={{ display: 'flex', gap: '0.2rem', alignItems: 'center' }}
      >
        <span>
          <span className="tooltip-title-assistant">NI Assistant</span>
          <span className="tooltip-tile-info-icon">
            <img
              src={AIIcon}
              alt="ai-icon"
              width={18}
              height={18}
              style={{ paddingTop: '1.1rem' }}
            />
          </span>
        </span>
      </Tooltip>
    </span>
  );

  return (
    <div className="ai-assistant">
      {showFabIcon && !newTab && (
        <Tooltip
          placement="left"
          title="Click here to get help from our AI powered chatbot"
        >
          <Fab
            aria-label="chat with us"
            className="ai-assistant-fab-icon"
            onClick={handleOpenAIAssistantModal}
          >
            <QuestionAnswerRoundedIcon />
          </Fab>
        </Tooltip>
      )}
      <Modal
        width="md"
        ref={modalRef}
        actionButtonText="New"
        enableCloseButton={false}
        defaultShowModal={newTab}
        enableActionButton={true}
        titleElement={TitleElement}
        enableCloseHeaderIcon={!newTab}
        enableNewTabHeaderIcon={!newTab}
        iconButtonColor="var(--color-main)"
        handleActionButton={handleActionButton}
        handleCloseAction={handleCloseActionButton}
        titleClassName="ai-assistant-title-container"
        mainContainerClassName={`${
          newTab ? 'ai-assistant-new-tab-full-screen' : ''
        }`}
        handleNewTab={() => openInNewTab('/#/ai-assistant')}
      >
        <Tabs
          value={selectedTab}
          onChange={handleChangeTabs}
          className="ai-assistant-tabs-container"
          classes={{
            indicator: 'ai-assistant-indicator-container',
          }}
        >
          {tabs.filter(_ => _ != "User Guide" && _ != "Trouble Ticket Assistant" ).map((el) => (
            <Tab
              key={el}
              value={el}
              label={el}
              className={`ai-assistant-tab-item`}
            />
          ))}
        </Tabs>
        <div className="ai-assistant-tab-content">
          <ChatBotProvider>
            <AIChatbot
              newTab={newTab}
              ref={smartTicketingRef}
              id={smartTicketingChatbotId.current}
              eventSourceRef={smartTicketingEventSourceRef}
              aiApplication={AIApplication.SMART_TICKETING}
              activeApplication={getActiveApplication(selectedTab)}
              handleClose={closeModal}
            />
          </ChatBotProvider>
          <ChatBotProvider>
            <AIChatbot
              newTab={newTab}
              ref={userGuideRef}
              id={userGuideChatbotId.current}
              eventSourceRef={userGuideEventSourceRef}
              aiApplication={AIApplication.USER_GUIDE}
              activeApplication={getActiveApplication(selectedTab)}
              handleClose={closeModal}
            />
          </ChatBotProvider>
          <ChatBotProvider>
            <AIChatbot
              newTab={newTab}
              ref={assistantRef}
              id={assistantChatbotId.current}
              eventSourceRef={assistantEventSourceRef}
              aiApplication={AIApplication.ASSISTANT}
              activeApplication={getActiveApplication(selectedTab)}
              handleClose={closeModal}
            />
          </ChatBotProvider>
          <ChatBotProvider>
            <AIChatbot
              newTab={newTab}
              ref={integrationRef}
              id={integrationChatbotId.current}
              eventSourceRef={integrationEventSourceRef}
              aiApplication={AIApplication.INTEGRATION_ASSISTANT}
              activeApplication={getActiveApplication(selectedTab)}
              handleClose={closeModal}
            />
          </ChatBotProvider>
        </div>
      </Modal>
    </div>
  );
};

export default forwardRef<Ref, Props>(AIAssistant);
